import { Directory, Filesystem } from '@capacitor/filesystem'
import { CapacitorZip } from '@capgo/capacitor-zip'
import type { SqliteExecutor } from '@/data/sqliteExecutor.ts'
import { ENTITY_REPLICA_CONFIGS } from '@/data/entityReplicaConfig.generated.ts'
import type { DeltaPage } from './catalogReplica.ts'
import type { ProgressReport } from './catalogBootstrap.ts'
import {
  COVER_DIRECTORY,
  COVER_SUBDIR,
  coverKeyFromFileName,
  coverPath,
  isNative,
} from './coverCache.ts'

// Prebuilt offline cover pack (OFFLINE_MODE_PLAN.md Phase 5): per-(type, tier)
// ZIP archives of budget-encoded WebPs, served by the cms /offline-images route.
// Each archive is downloaded once, written to a temp file, and unpacked with ONE
// native call (@capgo/capacitor-zip) into the same covers/ Filesystem store
// cache-on-view writes to — per-cover Filesystem.writeFile calls were bridge-bound
// (~thousands of round-trips). The zip's entry names already are the store's file
// names (encodeURIComponent(coverKey).webp), so an unzip lands resolver-ready.

const IMAGES_BASE_URL = import.meta.env.VITE_OFFLINE_IMAGES_BASE_URL as string | undefined
const IMAGES_FORMAT_VERSION = 2

export type PackTier = '96' | '192' | '384'
export type PackOutcome = 'applied' | 'skipped'

type ArchiveManifestEntry = {
  type: string
  tier: string
  file: string
  bytes: number
  count: number
  sha256: string
}

type ImagesManifest = {
  formatVersion: number
  archives: ArchiveManifestEntry[]
}

const COVER_BEARING_ENTITIES = new Set([
  'Novelty',
  'ReleaseEntry',
  'ReleaseItem',
  'ReleaseItemCollection',
])

const COVER_BEARING_CONFIGS = ENTITY_REPLICA_CONFIGS.filter((config) =>
  COVER_BEARING_ENTITIES.has(config.entity),
)

function coverKeyOf(coverFile: unknown): string | null {
  if (typeof coverFile !== 'string') return null
  const key = coverFile.split('.')[0].trim()
  return key.length > 0 ? key : null
}

// The cover keys a delta page delivers, in resolver preference order (cover wins
// over originalCover, matching coverKeyFromCover) — these are the pack entries a
// tier-enabled client should fetch individually between nightly archive builds.
export function collectCoverKeys(page: DeltaPage): string[] {
  const keys = new Set<string>()
  for (const config of COVER_BEARING_CONFIGS) {
    const items = page[config.field]
    if (!Array.isArray(items)) continue
    for (const item of items as Record<string, unknown>[]) {
      const key = coverKeyOf(item.cover) ?? coverKeyOf(item.originalCover)
      if (key) keys.add(key)
    }
  }
  return [...keys]
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

async function toHex(buffer: ArrayBuffer): Promise<string> {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function fetchImagesManifest(): Promise<ImagesManifest | null> {
  if (!IMAGES_BASE_URL) return null
  try {
    const response = await fetch(`${IMAGES_BASE_URL}/images-manifest.json`)
    if (!response.ok) return null
    const manifest = (await response.json()) as ImagesManifest
    return manifest.formatVersion === IMAGES_FORMAT_VERSION ? manifest : null
  } catch {
    return null
  }
}

async function fetchArchiveZip(
  entry: ArchiveManifestEntry,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  const response = await fetch(`${IMAGES_BASE_URL}/archive/${entry.tier}/${entry.type}.zip`, {
    signal,
  })
  if (!response.ok) throw new Error(`Archive ${entry.type}@${entry.tier} fetch failed`)
  const buffer = await response.arrayBuffer()
  if (entry.sha256 && globalThis.crypto?.subtle) {
    const digest = await toHex(await crypto.subtle.digest('SHA-256', buffer))
    if (digest !== entry.sha256)
      throw new Error(`Archive ${entry.type}@${entry.tier} sha256 mismatch`)
  }
  return new Uint8Array(buffer)
}

// Multiple of 3 so every chunk is standalone-valid base64 for append writes.
const ZIP_WRITE_CHUNK_BYTES = 3 * 1024 * 1024

async function writeTempZip(fileName: string, bytes: Uint8Array): Promise<string> {
  const path = `cover-pack/${fileName}`
  for (let start = 0; start < bytes.length; start += ZIP_WRITE_CHUNK_BYTES) {
    const data = toBase64(bytes.subarray(start, start + ZIP_WRITE_CHUNK_BYTES))
    // writeFile has no append option (Capacitor 8) — the first chunk creates the
    // file, later chunks must go through appendFile or they'd overwrite it and
    // the temp zip would end up as only its last 3MB slice.
    if (start === 0) {
      await Filesystem.writeFile({ path, directory: Directory.Cache, data, recursive: true })
    } else {
      await Filesystem.appendFile({ path, directory: Directory.Cache, data })
    }
  }
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache })
  return uri
}

function filesystemPathOf(uri: string): string {
  return uri.startsWith('file://') ? uri.slice('file://'.length) : uri
}

async function coversDirectoryPath(): Promise<string> {
  try {
    await Filesystem.mkdir({ path: COVER_SUBDIR, directory: COVER_DIRECTORY, recursive: true })
  } catch {
    // Already exists.
  }
  const { uri } = await Filesystem.getUri({ path: COVER_SUBDIR, directory: COVER_DIRECTORY })
  return filesystemPathOf(uri)
}

async function listCachedCoverKeys(): Promise<Set<string>> {
  try {
    const { files } = await Filesystem.readdir({ path: COVER_SUBDIR, directory: COVER_DIRECTORY })
    const keys = new Set<string>()
    for (const file of files) {
      const key = coverKeyFromFileName(file.name)
      if (key) keys.add(key)
    }
    return keys
  } catch {
    return new Set()
  }
}

async function writeCover(coverKey: string, bytes: Uint8Array): Promise<void> {
  await Filesystem.writeFile({
    path: coverPath(coverKey),
    directory: COVER_DIRECTORY,
    data: toBase64(bytes),
    recursive: true,
  })
}

// Downloads + unpacks the subscribed types' archives for a tier. The unzip
// overwrites existing files, so a tier switch (same keys, different encode)
// needs no special casing — every archive key ends up at the requested tier.
// An aborted signal (user changed the tier mid-download) stops between archives
// and cancels the in-flight fetch; the partial store is harmless (mixed tiers
// for some keys) because the retriggered download overwrites everything.
export async function downloadCoverArchives(opts: {
  types: string[]
  tier: PackTier
  signal?: AbortSignal
  report?: ProgressReport
}): Promise<PackOutcome> {
  if (!isNative() || opts.types.length === 0) return 'skipped'
  const manifest = await fetchImagesManifest()
  if (!manifest) return 'skipped'

  const byType = new Map(
    manifest.archives
      .filter((entry) => entry.tier === opts.tier)
      .map((entry) => [entry.type, entry]),
  )
  const entries = opts.types.map((type) => byType.get(type))
  if (entries.some((entry) => entry === undefined)) return 'skipped'
  const needed = (entries as ArchiveManifestEntry[]).filter((entry) => entry.count > 0)

  const total = needed.length
  let done = 0
  opts.report?.({ phase: 'images', done, total })

  try {
    const destination = await coversDirectoryPath()
    for (const entry of needed) {
      if (opts.signal?.aborted) return 'skipped'
      const zipBytes = await fetchArchiveZip(entry, opts.signal)
      // Unique per download so overlapping runs never share/clobber a temp zip
      // mid-write/unzip.
      const tempZipName = `${entry.type}-${crypto.randomUUID()}.zip`
      const zipUri = await writeTempZip(tempZipName, zipBytes)
      await CapacitorZip.unzip({ source: filesystemPathOf(zipUri), destination })
      try {
        await Filesystem.deleteFile({ path: `cover-pack/${tempZipName}`, directory: Directory.Cache })
      } catch {
        // Temp cleanup only — the Cache dir is reclaimable anyway.
      }
      done += 1
      opts.report?.({ phase: 'images', done, total })
    }
  } catch (error) {
    if (!opts.signal?.aborted) console.warn('Cover pack download failed', error)
    return 'skipped'
  }
  return 'applied'
}

// Between archive rebuilds, a feed-delivered new cover key is fetched as a single
// file; failures are silent (the resolver falls back to ThumbHash and a later
// sync retries via the same missing-key check).
export async function fetchMissingCovers(coverKeys: string[], tier: PackTier): Promise<void> {
  if (!isNative() || !IMAGES_BASE_URL || coverKeys.length === 0) return
  const existing = await listCachedCoverKeys()
  for (const coverKey of coverKeys) {
    if (existing.has(coverKey)) continue
    try {
      const response = await fetch(
        `${IMAGES_BASE_URL}/cover/${tier}/${encodeURIComponent(coverKey)}.webp`,
      )
      if (!response.ok) continue
      await writeCover(coverKey, new Uint8Array(await response.arrayBuffer()))
    } catch {
      continue
    }
  }
}

async function liveCoverKeys(executor: SqliteExecutor): Promise<Set<string>> {
  const keys = new Set<string>()
  for (const config of COVER_BEARING_CONFIGS) {
    const rows = await executor.query(
      `SELECT json_extract(data, '$.cover') AS cover, json_extract(data, '$.originalCover') AS originalCover FROM "${config.table}"`,
      [],
    )
    for (const row of rows) {
      const cover = coverKeyOf(row.cover)
      if (cover) keys.add(cover)
      const originalCover = coverKeyOf(row.originalCover)
      if (originalCover) keys.add(originalCover)
    }
  }
  return keys
}

function hasOriginalCover(config: (typeof COVER_BEARING_CONFIGS)[number]): boolean {
  return config.columns.some((column) => column.col === 'originalCover')
}

// The cover strings (full file names, not stripped keys) a page is about to
// supersede — read BEFORE applyDelta overwrites/deletes the rows, so the post-sync
// orphan check can tell which covers a change stranded. cover/originalCover are the
// exact values other rows would still hold, and the id filter is the primary key,
// so this stays O(rows the page touches), not O(catalog).
export async function collectSupersededCoverStrings(
  executor: SqliteExecutor,
  page: DeltaPage,
): Promise<string[]> {
  const strings: string[] = []
  for (const config of COVER_BEARING_CONFIGS) {
    const ids = new Set<string>()
    const items = page[config.field]
    if (Array.isArray(items)) {
      for (const item of items as Record<string, unknown>[]) {
        if (typeof item.id === 'string') ids.add(item.id)
      }
    }
    for (const tombstone of page.tombstones) {
      if (tombstone.entity === config.entity) ids.add(tombstone.rowId)
    }
    if (ids.size === 0) continue
    const cols = hasOriginalCover(config) ? 'cover, originalCover' : 'cover, NULL AS originalCover'
    const placeholders = [...ids].map(() => '?').join(', ')
    const rows = await executor.query(
      `SELECT ${cols} FROM "${config.table}" WHERE id IN (${placeholders})`,
      [...ids],
    )
    for (const row of rows) {
      if (typeof row.cover === 'string' && row.cover.length > 0) strings.push(row.cover)
      if (typeof row.originalCover === 'string' && row.originalCover.length > 0)
        strings.push(row.originalCover)
    }
  }
  return strings
}

// Of the superseded cover strings, the keys no replicated row references any more.
// One indexed probe per table (cover/originalCover are indexed columns, IN uses the
// index) over just the changed covers — the reference test is by stripped key, so a
// still-referenced sibling row (same cover on another entity) keeps the file.
async function orphanedCoverKeys(
  executor: SqliteExecutor,
  supersededStrings: Iterable<string>,
): Promise<Set<string>> {
  const candidates = [...new Set(supersededStrings)]
  if (candidates.length === 0) return new Set()
  const referencedKeys = new Set<string>()
  const placeholders = candidates.map(() => '?').join(', ')
  for (const config of COVER_BEARING_CONFIGS) {
    const withOriginal = hasOriginalCover(config)
    const cols = withOriginal ? 'cover, originalCover' : 'cover, NULL AS originalCover'
    const where = withOriginal
      ? `cover IN (${placeholders}) OR originalCover IN (${placeholders})`
      : `cover IN (${placeholders})`
    const params = withOriginal ? [...candidates, ...candidates] : candidates
    const rows = await executor.query(`SELECT ${cols} FROM "${config.table}" WHERE ${where}`, params)
    for (const row of rows) {
      const cover = coverKeyOf(row.cover)
      if (cover) referencedKeys.add(cover)
      const originalCover = coverKeyOf(row.originalCover)
      if (originalCover) referencedKeys.add(originalCover)
    }
  }
  const orphanKeys = new Set<string>()
  for (const candidate of candidates) {
    const key = coverKeyOf(candidate)
    if (key && !referencedKeys.has(key)) orphanKeys.add(key)
  }
  return orphanKeys
}

async function deleteCoverKeys(coverKeys: Iterable<string>): Promise<void> {
  for (const coverKey of coverKeys) {
    try {
      await Filesystem.deleteFile({ path: coverPath(coverKey), directory: COVER_DIRECTORY })
    } catch {
      // Already gone — nothing to reclaim.
    }
  }
}

// Targeted orphan prune driven by a sync's deltas: delete only the cover files a
// change stranded (a re-uploaded cover's old key, a tombstoned row's cover), and
// only once no replicated row still references them. Replaces the periodic full
// scan for the steady state; the resync path still falls back to pruneCoverCache.
export async function pruneSupersededCovers(
  executor: SqliteExecutor,
  supersededStrings: Iterable<string>,
): Promise<void> {
  if (!isNative()) return
  await deleteCoverKeys(await orphanedCoverKeys(executor, supersededStrings))
}

// One pass reclaims both pack orphans and cache-on-view orphans (shared covers/
// dir): anything whose cover key no longer appears on a replicated row — a
// re-uploaded cover's old timestamped key, a pruned subscription's rows — is dead.
// Full-scan fallback used after a resync, where per-delta superseded-key tracking
// can't run (the wipe leaves no old rows to read).
export async function pruneCoverCache(executor: SqliteExecutor): Promise<void> {
  if (!isNative()) return
  const live = await liveCoverKeys(executor)
  const cached = await listCachedCoverKeys()
  for (const coverKey of cached) {
    if (live.has(coverKey)) continue
    try {
      await Filesystem.deleteFile({ path: coverPath(coverKey), directory: COVER_DIRECTORY })
    } catch {
      // Already gone — nothing to reclaim.
    }
  }
}

// Total bytes the cover store occupies on disk — readdir reports per-file size, so
// one native call sums the whole covers/ dir (pack + cache-on-view files alike).
export async function getCoverStoreBytes(): Promise<number> {
  if (!isNative()) return 0
  try {
    const { files } = await Filesystem.readdir({ path: COVER_SUBDIR, directory: COVER_DIRECTORY })
    return files.reduce((sum, file) => sum + (file.size ?? 0), 0)
  } catch {
    return 0
  }
}

// Explicit "no pack / free the space" path (tier switched to none).
export async function clearCoverCache(): Promise<void> {
  if (!isNative()) return
  try {
    await Filesystem.rmdir({ path: COVER_SUBDIR, directory: COVER_DIRECTORY, recursive: true })
  } catch {
    // Directory never created — nothing to clear.
  }
}
